import { Box, Divider, Grow, ITheme, SvgIcon, Typography, useTheme } from '@mui/material'
import React, { useEffect, useId, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/reducers/store'
import { Utilities } from '../../../utility/utils';
import LeoTag from '../../common/LeoTag';
import { ReactComponent as slidersIcon } from '../../../assets/sliders.svg';
import { ReactComponent as camembertIcon } from '../../../assets/camembert.svg';
import { ReactComponent as worldIcon } from '../../../assets/world.svg';
import { ReactComponent as moneyIcon } from '../../../assets/donation.svg';
import { ReactComponent as validIcon } from '../../../assets/valid.svg';
import GeographicDistributionGraph from '../../common/GeographicDistributionGraph';
import FundSelectionList from './FundSelectionList';
import { useTranslation } from 'react-i18next';
import { addFundToPortfolio } from '../../../store/actions/createPortfolio';
import { SectorAffectation } from '../../../models/Portfolio';
import { FundItem } from '../../../models/Fund';
import { GeographicDistribution, Stages } from '../../../models/Common';
import useIsFullable from '../../../hooks/useIsFullable';
import LeoAiOverlay from '../../common/LeoAiOverlay';
import StageGraph from '../../common/StageGraph';
import FundSelectionComplianceProgress from './FundSelectionComplianceProgress';
import useComplianceProportion from '../../../hooks/useComplianceProportion';

export interface IFundSelectionConstructionProps { }

const FundSelectionConstruction: React.FC<IFundSelectionConstructionProps> = () => {
    const portfolio = useAppSelector(state => state.createPortfolio.portfolio);
    const funds = useAppSelector(state => state.libraryOfFundsReducer.funds);
    const isDragging = useAppSelector(state => state.createPortfolio.isDragging);
    const isFullable = useIsFullable();
    const fullableProportion = useComplianceProportion();
    const theme = useTheme() as ITheme;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const fundsListRef = useRef<HTMLDivElement>(null);
    const [showAiToolTip, setShowAiToolTip] = useState(false);

    useEffect(() => {
        setShowAiToolTip(!isFullable && fundsListRef.current !== null);
    }, [isFullable, fundsListRef.current]);

    const onFundDropped = (event: React.DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        const data = event.dataTransfer.getData("Id");
        const idAsNumber = parseInt(data);
        if (!isNaN(idAsNumber)) {
            dispatch(addFundToPortfolio(idAsNumber));
        }
    }

    if (portfolio === undefined || funds === undefined) {
        return <></>;
    }
    const selectedFunds = portfolio.funds.map(sf => {
        const f = funds.find(f => f.id === sf.id);
        if (f) {
            return { fund: f, proportion: sf.proportion };
        } else {
            return undefined;
        }
    }).filter(f => f !== undefined) as { fund: FundItem, proportion: number }[];
    const sectors: SectorAffectation[] = [];
    const geo: GeographicDistribution[] = [];
    const stages: Stages = { early: false, growth: false, late: false };
    for (const fund of selectedFunds) {
        for (const sector of fund.fund.sectorDistribution) {
            const index = sectors.findIndex(s => s.name === sector.sector);
            if (index === -1) {
                sectors.push({ icon: sector.icon, name: sector.sector, proportion: sector.proportion * fund.proportion, id: 0 });
            } else {
                sectors[index].proportion += sector.proportion * fund.proportion;
            }
        }
        for (const distrib of fund.fund.geographicDistribution) {
            const index = geo.findIndex(s => s.zone === distrib.zone);
            if (index === -1) {
                geo.push({ icon: distrib.icon, zone: distrib.zone, proportion: distrib.proportion * fund.proportion });
            } else {
                geo[index].proportion += distrib.proportion * fund.proportion;
            }
        }
        stages.early = stages.early || fund.fund.stageDistribution.early > 0;
        stages.growth = stages.growth || fund.fund.stageDistribution.growth > 0;
        stages.late = stages.late || fund.fund.stageDistribution.late > 0;
    }


    return <Box display={"flex"} flexDirection={"column"} width={"100%"} bgcolor={theme.palette.background.paper} padding={"12px 26px"} borderRadius={"8px"} gap={"21px"}>
        <Box height={"22px"} marginTop={"-29px"} display={"flex"} gap={"4px"}>
            {portfolio.retailInvestors && <Box height={"22px"} width={"auto"} padding={"0 3px"} bgcolor={theme.palette.primary.dark} alignSelf={"self-start"} borderRadius={"4px"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                <Typography color={theme.palette.common.white} fontSize={"11px"} lineHeight={"20px"} fontWeight={"600"}>{t('LibraryOfFundsELTIF')}</Typography>
            </Box>}
        </Box>
        <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
            <Typography fontSize={"24px"} lineHeight={"29.26px"} fontWeight={"700"}>{portfolio.name}</Typography>
            <Box display={"flex"} flexDirection={"column"} alignItems={"flex-end"} gap={"6px"}>
                <Typography fontSize={"22px"} lineHeight={"26.82px"} fontWeight={"600"}>€{Utilities.toFriendlyMoneyValue(portfolio.fundraisingTarget)}</Typography>
                <Typography fontSize={"12px"} lineHeight={"10px"} fontWeight={"600"}>{t('PortfolioPageItemFundraisingTarget')}</Typography>
            </Box>
        </Box>
        <Divider sx={{ margin: "0 -26px" }} />
        <Box display={"flex"} gap={"17px"}>
            <SvgIcon component={slidersIcon} inheritViewBox width={"20"} />
            <Typography fontSize={"18px"} lineHeight={"21.94px"} fontWeight={"500"}>{t('PortfolioPageItemSectorDistribution')}</Typography>
        </Box>
        <Divider />
        <Box display={"flex"} gap={"12px"} flexWrap={"wrap"}>
            {sectors.map(s => <LeoTag key={`sector-${s.name}`} text={s.name} icon={s.icon} size='medium' />)}
        </Box>
        <Box display={"flex"} gap={"17px"}>
            <SvgIcon component={camembertIcon} inheritViewBox width={"20"} />
            <Typography fontSize={"18px"} lineHeight={"21.94px"} fontWeight={"500"}>{t('PortfolioPageItemStageDistribution')}</Typography>
        </Box>
        <Divider />
        <StageGraph data={stages} />
        <Box display={"flex"} gap={"17px"}>
            <SvgIcon component={worldIcon} inheritViewBox width={"20"} />
            <Typography fontSize={"18px"} lineHeight={"21.94px"} fontWeight={"500"}>{t('PortfolioPageItemGeographicDistribution')}</Typography>
        </Box>
        <Divider />
        <GeographicDistributionGraph distribution={geo} hideProportion />
        {portfolio.retailInvestors && <>
            <Box display={"flex"} gap={"17px"}>
                <SvgIcon component={validIcon} inheritViewBox width={"20"} />
                <Typography fontSize={"18px"} lineHeight={"21.94px"} fontWeight={"500"}>{t('CreatePortfolioELTIFCompliance')}</Typography>
            </Box>
            <Divider />
            <FundSelectionComplianceProgress progress={fullableProportion} />
        </>}
        <Box display={"flex"} gap={"17px"}>
            <SvgIcon component={moneyIcon} inheritViewBox width={"20"} />
            <Typography fontSize={"18px"} lineHeight={"21.94px"} fontWeight={"500"}>{t('CreatePortfolioFundsSelected')} ({portfolio.funds.length}/{isNaN(portfolio.fundCount) || portfolio.fundCount === undefined ? t('CreatePortfolioAnyLabel') : portfolio.fundCount})</Typography>
        </Box>
        <Divider />
        {showAiToolTip && <>
            {portfolio.funds.length === 0 ?
                <LeoAiOverlay target={fundsListRef} orientation='reverse' delay={2000}>
                    <p>{t('CreatePortfolioAiAssistantWelcome1')}</p>
                    <p>{t('CreatePortfolioAiAssistantWelcome2')}</p>
                    <p>{t('CreatePortfolioAiAssistantWelcome3')}</p>
                </LeoAiOverlay>
                :
                <LeoAiOverlay target={fundsListRef}>
                    <p>{t('CreatePortfolioAiAssistantAddFunds')}</p>
                </LeoAiOverlay>
            }
        </>}
        <FundSelectionList portfolioName={portfolio.name} funds={portfolio.funds} onDrop={onFundDropped} isDroppable={isDragging} fundsListRef={fundsListRef} />
    </Box>
}

export default FundSelectionConstruction